import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRegisterOmlContext } from '@/hooks/useRegisterOmlContext';
import { useOmlAccess, useOmlMutations } from '@/hooks/useOml';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Gate de /owned-media. El acceso lo resuelve el governance N3 (OML_GOVERNANCE +
 * asignaciones vigentes + grants): quien no lo tiene NO ve el apartado en el
 * menú y, si entra por URL directa, recibe un mensaje explícito de sin acceso y
 * el intento queda en auditoría (AC-4) — nunca una página vacía.
 */
export function OmlProtectedRoute() {
  const { t } = useTranslation();
  const { data: access, isLoading, refetch } = useOmlAccess();
  const { logDenied } = useOmlMutations();
  const location = useLocation();
  const degraded = !!access?.degraded;
  const denied = !isLoading && !!access && !access.canView && !degraded;
  // supportIA del costado responde sobre OML mientras el apartado está abierto (08/10/2026).
  useRegisterOmlContext(!!access?.canView && !degraded);

  useEffect(() => {
    if (denied) logDenied.mutate({ path: location.pathname });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [denied, location.pathname]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }
  if (!access) return <Navigate to="/dashboard" replace />;
  if (degraded) {
    return (
      <div className="max-w-xl mx-auto mt-16 px-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('oml.denied.degraded_title', 'No pudimos verificar tu acceso')}</AlertTitle>
          <AlertDescription>
            {t('oml.denied.degraded_body', 'Falló la lectura de tus asignaciones o permisos. No es un "sin acceso": reintentá en unos segundos y, si persiste, avisá a Internal.')}
            <div className="mt-3"><Button size="sm" variant="outline" onClick={() => refetch()}>{t('common.retry', 'Reintentar')}</Button></div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  if (!access.canView) {
    return (
      <div className="max-w-xl mx-auto mt-16 px-4">
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertTitle>{t('oml.denied.title', 'Sin acceso a Owned Media')}</AlertTitle>
          <AlertDescription>
            {t('oml.denied.body', 'Este apartado es confidencial de negocio (N3). Lo ven Owned Media, Retention, Growth y Dirección; el resto pide acceso nominal y lo aprueba el data owner. Este intento quedó registrado.')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  return <Outlet />;
}
